import { apiHelpers } from '../utils/api';
import {
  ChartOfAccount,
  ChartOfAccountFormData,
  ChartOfAccountStats,
  ChartOfAccountsResponse,
  ChartOfAccountFilters,
  AccountTypeOption
} from '../types/chartOfAccount';

export const chartOfAccountService = {
  async getChartOfAccounts(filters: ChartOfAccountFilters = {}): Promise<ChartOfAccountsResponse> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.accountType) params.append('accountType', filters.accountType);
    if (filters.isActive !== undefined) params.append('isActive', String(filters.isActive));
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    return await apiHelpers.get(`/chart-of-accounts?${params.toString()}`);
  },

  async getChartOfAccount(id: string): Promise<ChartOfAccount> {
    return await apiHelpers.get(`/chart-of-accounts/${id}`);
  },

  async createChartOfAccount(data: ChartOfAccountFormData): Promise<ChartOfAccount> {
    return await apiHelpers.post('/chart-of-accounts', data);
  },

  async updateChartOfAccount(id: string, data: Partial<ChartOfAccountFormData>): Promise<ChartOfAccount> {
    return await apiHelpers.put(`/chart-of-accounts/${id}`, data);
  },

  async deleteChartOfAccount(id: string): Promise<void> {
    return await apiHelpers.delete(`/chart-of-accounts/${id}`);
  },

  async getChartOfAccountStats(): Promise<ChartOfAccountStats> {
    return await apiHelpers.get('/chart-of-accounts/stats');
  },

  async getAccountTypes(): Promise<AccountTypeOption[]> {
    return await apiHelpers.get('/chart-of-accounts/types');
  },

  // Helper functions
  getAccountTypeLabel(type: string): string {
    switch (type) {
      case 'ASSET': return 'Asset';
      case 'LIABILITY': return 'Liability';
      case 'EQUITY': return 'Equity';
      case 'INCOME': return 'Income';
      case 'EXPENSE': return 'Expense';
      default: return type;
    }
  },

  getAccountTypeColor(type: string): string {
    switch (type) {
      case 'ASSET': return 'bg-green-100 text-green-800';
      case 'LIABILITY': return 'bg-red-100 text-red-800';
      case 'EQUITY': return 'bg-blue-100 text-blue-800';
      case 'INCOME': return 'bg-purple-100 text-purple-800';
      case 'EXPENSE': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  },

  // Format account code for display
  formatAccountCode(code: string): string {
    return code.padStart(4, '0');
  },

  // Get account hierarchy path
  getAccountPath(account: ChartOfAccount): string {
    if (account.parent) {
      return `${account.parent.code} - ${account.parent.name} > ${account.code} - ${account.name}`;
    }
    return `${account.code} - ${account.name}`;
  },

  // Check if account can be deleted
  canDeleteAccount(account: ChartOfAccount): boolean {
    const hasTransactions = (account._count?.journalEntryLines || 0) > 0;
    const hasChildren = (account._count?.children || 0) > 0;
    return !hasTransactions && !hasChildren;
  },

  // Get account type description
  getAccountTypeDescription(type: string): string {
    switch (type) {
      case 'ASSET':
        return 'Resources owned by the business (cash, inventory, equipment, etc.)';
      case 'LIABILITY':
        return 'Debts and obligations owed to others (loans, accounts payable, etc.)';
      case 'EQUITY':
        return 'Owner\'s stake in the business (capital, retained earnings, etc.)';
      case 'INCOME':
        return 'Revenue generated from business operations (sales, service revenue, etc.)';
      case 'EXPENSE':
        return 'Costs incurred in running the business (rent, utilities, salaries, etc.)';
      default:
        return '';
    }
  }
};